import { ContentInventory } from '../../hooks/useInventory';

export type InventoryTemplateSelection = {
    inventoryId: number;
    versionId: number;
};

/**
 * Reconcile saved template selections against the currently loaded content inventory:
 * - drops references to templates/variations that no longer exist or are not ACTIVE
 *   (they cannot be rendered as rows, so they would be counted but never shown as checked)
 * - collapses duplicate references
 * - normalises ids to the inventory's canonical values, so strict-equality checks against
 *   inventory rows match even if the saved copy drifted to string ids
 *
 * `changed` is true when the reconciled list differs from the input, i.e. the caller
 * should write it back; reconciling an already-clean list reports no change so callers
 * can safely reconcile on every render without looping.
 */
export const reconcileInventoryTemplateSelections = (
    inventory: ContentInventory[],
    selected: InventoryTemplateSelection[],
): { reconciled: InventoryTemplateSelection[]; changed: boolean } => {
    const canonical = new Map<string, InventoryTemplateSelection>();
    inventory.forEach(inv => {
        (inv.versions || [])
            .filter(v => v.status === 'ACTIVE')
            .forEach(v => {
                canonical.set(`${inv.inventoryId}:${v.inventorySubId}`, {
                    inventoryId: inv.inventoryId,
                    versionId: v.inventorySubId,
                });
            });
    });

    const seen = new Set<string>();
    const reconciled: InventoryTemplateSelection[] = [];
    selected.forEach(s => {
        const key = `${s.inventoryId}:${s.versionId}`;
        const match = canonical.get(key);
        if (match && !seen.has(key)) {
            seen.add(key);
            reconciled.push(match);
        }
    });

    const changed =
        reconciled.length !== selected.length ||
        reconciled.some((r, i) => r.inventoryId !== selected[i].inventoryId || r.versionId !== selected[i].versionId);

    return { reconciled, changed };
};

/**
 * Distinct tags across all ACTIVE template variations, deduplicated case-insensitively
 * (first-seen casing wins) and sorted alphabetically. Feeds the strict tag pickers.
 */
export const distinctInventoryTags = (inventory: ContentInventory[]): string[] => {
    const seen = new Map<string, string>();

    inventory.forEach(inv => {
        (inv.versions || [])
            .filter(v => v.status === 'ACTIVE')
            .forEach(v => {
                (v.tags || []).forEach(t => {
                    const tag = (t.tagText || '').trim();
                    if (!tag) {
                        return;
                    }
                    const key = tag.toLowerCase();
                    if (!seen.has(key)) {
                        seen.set(key, tag);
                    }
                });
            });
    });

    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
};

/**
 * Counts ACTIVE template variations carrying at least one of the given tags
 * (ANY/union, case-insensitive) - mirrors the backend's run-time pool resolution.
 * With paidOnly, only variations with a price are counted.
 */
export const countActiveVersionsMatchingTags = (inventory: ContentInventory[], tags: string[], paidOnly = false): number => {
    const wanted = new Set(tags.map(t => t.trim().toLowerCase()).filter(Boolean));

    if (wanted.size === 0) {
        return 0;
    }

    let count = 0;

    inventory.forEach(inv => {
        (inv.versions || [])
            .filter(v => v.status === 'ACTIVE' && (!paidOnly || Number(v.price || 0) > 0))
            .forEach(v => {
                if ((v.tags || []).some(t => wanted.has((t.tagText || '').trim().toLowerCase()))) {
                    count += 1;
                }
            });
    });

    return count;
};
